import React, { useState, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { BarChart3, Table, PieChart, LineChart, Eye, ChevronDown, ChevronUp, TrendingUp, CheckCircle, XCircle, Clock } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const VisualizerNode = ({ data, isConnectable, selected }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [visualizationType, setVisualizationType] = useState(data.visualizationType || 'summary');
  const [processedData, setProcessedData] = useState(null);

  // Parse execution results if available
  let executionResult = null;
  let executionStatus = null;
  
  if (data.executionResult) {
    try {
      executionResult = typeof data.executionResult === 'string' 
        ? JSON.parse(data.executionResult) 
        : data.executionResult;
      executionStatus = data.executionStatus || 'completed';
    } catch (e) {
      console.error('Error parsing execution result:', e);
    }
  }

  // Process data for visualization
  useEffect(() => {
    if (executionResult) {
      
      // Try to find the actual data in various possible structures
      let dataToVisualize = null;
      
      // First priority: check for the output.data structure from the backend visualizer node
      if (executionResult.output && executionResult.output.data) {
        dataToVisualize = executionResult.output.data;
      }
      // Second priority: check for direct data fields
      else if (executionResult.data) {
        dataToVisualize = executionResult.data;
      }
      // Third priority: check for output structure (from other node types)
      else if (executionResult.output) {
        dataToVisualize = executionResult.output;
      }
      // Fourth priority: check for inputData
      else if (executionResult.inputData) {
        dataToVisualize = executionResult.inputData;
      }
      // Last resort: the execution result itself is the data
      else if (Array.isArray(executionResult) || typeof executionResult === 'object') {
        dataToVisualize = executionResult;
      }
      
      if (dataToVisualize) {
        processDataForVisualization(dataToVisualize);
      }
    }
  }, [executionResult]);

  // Enhanced business metrics extraction
  const extractBusinessMetrics = (inputData) => {
    const metrics = {
      sourceNodeType: 'unknown',
      metrics: [],
      summaryStats: {},
      chartData: [],
      rawData: inputData
    };

    // Detect source node type and extract relevant metrics
    if (inputData.type === 'autoEvaluatorNode' || (inputData.output && inputData.output.evaluations)) {
      metrics.sourceNodeType = 'autoEvaluator';
      const evaluations = inputData.output?.results || inputData.output?.evaluations || [];
      
      metrics.metrics = evaluations.map((evaluation, index) => ({
        id: evaluation.testCaseId || index,
        score: evaluation.score || 0,
        passed: evaluation.passed || false,
        response: evaluation.response || 'N/A',
        input: evaluation.input || 'N/A',
        reasoning: evaluation.reasoning || 'N/A',
        criteriaScores: evaluation.criteriaScores || {},
        executionTime: evaluation.executionTime || 0,
        model: evaluation.model || inputData.output?.evaluation_model || 'N/A'
      }));

      // Summary statistics for auto-evaluator
      const scores = metrics.metrics.map(m => m.score).filter(s => s !== undefined && s !== null);
      const passCount = metrics.metrics.filter(m => m.passed).length;
      const executionTimes = metrics.metrics.map(m => m.executionTime).filter(t => t > 0);
      
      metrics.summaryStats = {
        averageScore: scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length) : 0,
        passRate: metrics.metrics.length > 0 ? ((passCount / metrics.metrics.length) * 100) : 0,
        totalEvaluations: metrics.metrics.length,
        passedCount: passCount,
        failedCount: metrics.metrics.length - passCount,
        threshold: inputData.output?.passThreshold || 0.7,
        averageExecutionTime: executionTimes.length > 0 ? Math.round(executionTimes.reduce((a, b) => a + b, 0) / executionTimes.length) : 0,
        criteria: inputData.data?.criteria || [],
        model: inputData.output?.evaluation_model || 'N/A'
      };

      // Chart data for score distribution and pass/fail
      metrics.chartData = {
        passFailData: [
          { label: 'Passed', value: passCount, color: 'rgb(34, 197, 94)' },
          { label: 'Failed', value: metrics.metrics.length - passCount, color: 'rgb(239, 68, 68)' }
        ],
        scoreDistribution: [
          { range: '90-100%', count: metrics.metrics.filter(m => m.score >= 0.9).length, color: 'rgb(34, 197, 94)' },
          { range: '70-89%', count: metrics.metrics.filter(m => m.score >= 0.7 && m.score < 0.9).length, color: 'rgb(59, 130, 246)' },
          { range: '50-69%', count: metrics.metrics.filter(m => m.score >= 0.5 && m.score < 0.7).length, color: 'rgb(245, 158, 11)' },
          { range: '0-49%', count: metrics.metrics.filter(m => m.score < 0.5).length, color: 'rgb(239, 68, 68)' }
        ].filter(range => range.count > 0),
        scoreTimeline: metrics.metrics.map((m, index) => ({
          x: index + 1,
          y: (m.score * 100).toFixed(1)
        }))
      };

    } else if (inputData.type === 'promptNode' || (inputData.output && (inputData.output.results || inputData.output.response))) {
      metrics.sourceNodeType = 'prompt';
      const results = inputData.output?.results || (inputData.output?.response ? [inputData.output] : []);
      
      metrics.metrics = results.map((result, index) => ({
        id: result.testCaseId || index,
        input: result.input || inputData.inputs?.inputText || 'N/A',
        response: result.response || result.output || 'N/A',
        model: result.model || inputData.output?.model || 'N/A',
        tokens: result.totalTokens || result.tokens || 0,
        inputTokens: result.inputTokens || 0,
        outputTokens: result.outputTokens || 0,
        executionTime: result.executionTime || 0,
        temperature: result.temperature || 'N/A',
        promptVersion: inputData.versionNumber || 'N/A',
        promptName: inputData.promptName || 'N/A'
      }));

      // Summary statistics for prompt node
      const tokenCounts = metrics.metrics.map(m => m.tokens).filter(t => t > 0);
      const executionTimes = metrics.metrics.map(m => m.executionTime).filter(t => t > 0);
      const inputTokens = metrics.metrics.map(m => m.inputTokens).filter(t => t > 0);
      const outputTokens = metrics.metrics.map(m => m.outputTokens).filter(t => t > 0);
      
      metrics.summaryStats = {
        totalTokens: tokenCounts.reduce((a, b) => a + b, 0),
        totalInputTokens: inputTokens.reduce((a, b) => a + b, 0),
        totalOutputTokens: outputTokens.reduce((a, b) => a + b, 0),
        averageTokens: tokenCounts.length > 0 ? Math.round(tokenCounts.reduce((a, b) => a + b, 0) / tokenCounts.length) : 0,
        averageExecutionTime: executionTimes.length > 0 ? Math.round(executionTimes.reduce((a, b) => a + b, 0) / executionTimes.length) : 0,
        totalRequests: metrics.metrics.length,
        model: metrics.metrics[0]?.model || 'Unknown',
        promptName: metrics.metrics[0]?.promptName || 'N/A',
        promptVersion: metrics.metrics[0]?.promptVersion || 'N/A'
      };

      // Chart data for token usage distribution and performance
      metrics.chartData = {
        tokenDistribution: [
          { range: '0-100', count: tokenCounts.filter(t => t <= 100).length, color: 'rgb(34, 197, 94)' },
          { range: '101-500', count: tokenCounts.filter(t => t > 100 && t <= 500).length, color: 'rgb(59, 130, 246)' },
          { range: '501-1000', count: tokenCounts.filter(t => t > 500 && t <= 1000).length, color: 'rgb(245, 158, 11)' },
          { range: '1000+', count: tokenCounts.filter(t => t > 1000).length, color: 'rgb(239, 68, 68)' }
        ].filter(range => range.count > 0),
        tokenUsageBreakdown: [
          { label: 'Input Tokens', value: metrics.summaryStats.totalInputTokens, color: 'rgb(59, 130, 246)' },
          { label: 'Output Tokens', value: metrics.summaryStats.totalOutputTokens, color: 'rgb(34, 197, 94)' }
        ].filter(item => item.value > 0),
        performanceTimeline: metrics.metrics.map((m, index) => ({
          x: index + 1,
          y: m.executionTime || 0
        }))
      };

    } else {
      // Fallback for unknown node types
      metrics.sourceNodeType = 'generic';
      const flattenedData = flattenObject(inputData);
      metrics.metrics = flattenedData.slice(0, 50);
      
      // Generate chart data for data types distribution
      const typeDistribution = {};
      flattenedData.forEach(item => {
        const type = item.type || 'unknown';
        typeDistribution[type] = (typeDistribution[type] || 0) + 1;
      });

      const colors = ['rgb(59, 130, 246)', 'rgb(34, 197, 94)', 'rgb(245, 158, 11)', 'rgb(239, 68, 68)', 'rgb(168, 85, 247)', 'rgb(236, 72, 153)'];
      metrics.chartData = {
        typeDistribution: Object.entries(typeDistribution).map(([type, count], index) => ({
        label: type.charAt(0).toUpperCase() + type.slice(1),
        value: count,
        color: colors[index % colors.length]
        }))
      };

      metrics.summaryStats = {
        totalProperties: flattenedData.length,
        maxDepth: Math.max(...flattenedData.map(item => item.depth || 0)),
        typeDistribution: typeDistribution
      };
    }

    return metrics;
  };

  const processDataForVisualization = (inputData) => {
    if (!inputData) {
      return;
    }

    // Extract business metrics based on node type
    const businessMetrics = extractBusinessMetrics(inputData);
    
    const processed = {
      type: businessMetrics.sourceNodeType,
      businessMetrics: businessMetrics,
      summary: businessMetrics.summaryStats
    };

    setProcessedData(processed);
  };

  // Helper function to flatten nested objects for generic data types
  const flattenObject = (obj, prefix = '', maxDepth = 3, currentDepth = 0) => {
    const flattened = [];
    
    if (currentDepth >= maxDepth) {
      flattened.push({
        key: prefix || 'root',
        value: JSON.stringify(obj).substring(0, 100) + (JSON.stringify(obj).length > 100 ? '...' : ''),
        type: 'object (max depth reached)',
        depth: currentDepth
      });
      return flattened;
    }
    
    if (Array.isArray(obj)) {
      flattened.push({
        key: prefix || 'root',
        value: `Array with ${obj.length} items`,
        type: 'array',
        depth: currentDepth
      });
      
      // Show first few array items
      obj.slice(0, 3).forEach((item, index) => {
        const key = prefix ? `${prefix}[${index}]` : `[${index}]`;
        if (typeof item === 'object' && item !== null) {
          flattened.push(...flattenObject(item, key, maxDepth, currentDepth + 1));
        } else {
          flattened.push({
            key: key,
            value: String(item),
            type: typeof item,
            depth: currentDepth + 1
          });
        }
      });
      
      if (obj.length > 3) {
        flattened.push({
          key: prefix ? `${prefix}[...]` : '[...]',
          value: `... and ${obj.length - 3} more items`,
          type: 'continuation',
          depth: currentDepth + 1
        });
      }
    } else if (typeof obj === 'object' && obj !== null) {
      const entries = Object.entries(obj);
      
      entries.slice(0, 10).forEach(([key, value]) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        
        if (typeof value === 'object' && value !== null) {
          flattened.push(...flattenObject(value, fullKey, maxDepth, currentDepth + 1));
        } else {
          flattened.push({
            key: fullKey,
            value: String(value),
            type: typeof value,
            depth: currentDepth
          });
        }
      });
      
      if (entries.length > 10) {
        flattened.push({
          key: prefix ? `${prefix}.[...]` : '[...]',
          value: `... and ${entries.length - 10} more properties`,
          type: 'continuation',
          depth: currentDepth
        });
      }
    } else {
      flattened.push({
        key: prefix || 'value',
        value: String(obj),
        type: typeof obj,
        depth: currentDepth
      });
    }
    
    return flattened;
  };



  const getStatusIcon = () => {
    switch (executionStatus) {
      case 'completed':
        return <CheckCircle className="h-3 w-3 text-blue-600" />;
      case 'failed':
        return <XCircle className="h-3 w-3 text-red-500" />;
      case 'running':
        return <Clock className="h-3 w-3 text-blue-500 animate-spin" />;
      default:
        return null;
    }
  };

  const getNodeBorderColor = () => {
    if (selected) {
      return 'border-blue-400 shadow-blue-200/50 shadow-lg';
    }
    
    switch (executionStatus) {
      case 'completed':
        return 'border-blue-300 shadow-blue-100/50';
      case 'failed':
        return 'border-red-300 shadow-red-100/50';
      case 'running':
        return 'border-blue-300 shadow-blue-100/50';
      default:
        return 'border-blue-200 shadow-blue-50/50';
    }
  };

  const renderVisualization = () => {
    if (!processedData) {
      return (
        <div className="text-xs text-gray-500 italic text-center py-4 space-y-2">
          <div>No data to visualize</div>
          {executionResult && (
            <div className="text-xs text-gray-400">
              Debug: Execution result exists but no valid data found
            </div>
          )}
          {!executionResult && (
            <div className="text-xs text-gray-400">
              Connect this node to a data source and run the flow
            </div>
          )}
        </div>
      );
    }

    switch (visualizationType) {
      case 'table':
        return renderTable();
      case 'summary':
        return renderSummary();
      case 'chart':
        return renderChart();
      default:
        return renderSummary();
    }
  };

  const renderTable = () => {
    if (!processedData || !processedData.businessMetrics) return null;

    const { businessMetrics } = processedData;
    const metrics = businessMetrics.metrics.slice(0, data.maxDisplayItems || 50);

    if (businessMetrics.sourceNodeType === 'autoEvaluator') {
      return (
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-700">
            Evaluation Results ({businessMetrics.metrics.length} total)
          </div>
          <div className="bg-gray-50 rounded border max-h-64 overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-100">
                <tr className="border-b border-gray-200">
                  <th className="text-left p-2 font-medium text-gray-700 min-w-16">Score</th>
                  <th className="text-left p-2 font-medium text-gray-700 min-w-16">Status</th>
                  <th className="text-left p-2 font-medium text-gray-700 min-w-32">Input</th>
                  <th className="text-left p-2 font-medium text-gray-700 min-w-32">Response</th>
                  <th className="text-left p-2 font-medium text-gray-700 min-w-32">Reasoning</th>
                </tr>
              </thead>
              <tbody>
                {metrics.map((metric, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-blue-50">
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded font-medium text-xs ${
                        metric.score >= (businessMetrics.summaryStats.threshold || 0.7) 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {(metric.score * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        metric.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {metric.passed ? '✓ Pass' : '✗ Fail'}
                      </span>
                    </td>
                    <td className="p-2 text-gray-900 text-xs">
                      <div className="truncate max-w-32" title={metric.input}>
                        {metric.input.length > 50 ? `${metric.input.substring(0, 50)}...` : metric.input}
                      </div>
                    </td>
                    <td className="p-2 text-gray-900 text-xs">
                      <div className="truncate max-w-32" title={metric.response}>
                        {metric.response.length > 50 ? `${metric.response.substring(0, 50)}...` : metric.response}
                      </div>
                    </td>
                    <td className="p-2 text-gray-900 text-xs">
                      <div className="truncate max-w-32" title={metric.reasoning}>
                        {metric.reasoning.length > 50 ? `${metric.reasoning.substring(0, 50)}...` : metric.reasoning}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    } else if (businessMetrics.sourceNodeType === 'prompt') {
      return (
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-700">
            Prompt Results ({businessMetrics.metrics.length} total)
          </div>
          <div className="bg-gray-50 rounded border max-h-64 overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-100">
                <tr className="border-b border-gray-200">
                  <th className="text-left p-2 font-medium text-gray-700 min-w-24">Input</th>
                  <th className="text-left p-2 font-medium text-gray-700 min-w-32">Response</th>
                  <th className="text-left p-2 font-medium text-gray-700 min-w-20">Model</th>
                  <th className="text-left p-2 font-medium text-gray-700 min-w-16">Tokens</th>
                  <th className="text-left p-2 font-medium text-gray-700 min-w-16">Time (ms)</th>
                </tr>
              </thead>
              <tbody>
                {metrics.map((metric, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-blue-50">
                    <td className="p-2 text-gray-900 text-xs">
                      <div className="truncate max-w-24" title={metric.input}>
                        {metric.input.length > 40 ? `${metric.input.substring(0, 40)}...` : metric.input}
                      </div>
                    </td>
                    <td className="p-2 text-gray-900 text-xs">
                      <div className="truncate max-w-32" title={metric.response}>
                        {metric.response.length > 60 ? `${metric.response.substring(0, 60)}...` : metric.response}
                      </div>
                    </td>
                    <td className="p-2 text-gray-700 text-xs font-mono">
                      {metric.model.includes('llama') ? '🦙' : '🤖'} {metric.model.split('-')[0]}
                    </td>
                    <td className="p-2 text-center">
                      <span className={`px-2 py-1 rounded text-xs ${
                        metric.tokens > 1000 ? 'bg-red-100 text-red-800' :
                        metric.tokens > 500 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {metric.tokens}
                      </span>
                    </td>
                    <td className="p-2 text-center text-gray-700 text-xs">
                      {metric.executionTime}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    } else {
      // Fallback to flattened data view for unknown types
      const flattenedData = businessMetrics.metrics.slice(0, data.maxDisplayItems || 50);
      return (
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-700">
            Data Properties ({businessMetrics.metrics.length} total)
          </div>
          <div className="bg-gray-50 rounded border max-h-64 overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-100">
                <tr className="border-b border-gray-200">
                  <th className="text-left p-2 font-medium text-gray-700 min-w-32">Property</th>
                  <th className="text-left p-2 font-medium text-gray-700 min-w-48">Value</th>
                </tr>
              </thead>
              <tbody>
                {flattenedData.map((item, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-blue-50">
                    <td className="p-2 text-gray-700 font-mono text-xs">{item.key}</td>
                    <td className="p-2 text-gray-900 text-xs">
                      <div className="truncate" title={item.value}>
                        {item.value && item.value.length > 100 ? `${item.value.substring(0, 100)}...` : item.value}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
  };

  const renderSummary = () => {
    if (!processedData || !processedData.businessMetrics) return null;

    const { businessMetrics } = processedData;
    const stats = businessMetrics.summaryStats;

    if (businessMetrics.sourceNodeType === 'autoEvaluator') {
      return (
        <div className="space-y-4">
          <div className="text-xs font-medium text-gray-700">Auto-Evaluator Summary</div>
          
          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="text-lg font-bold text-green-900">{stats.passRate.toFixed(1)}%</div>
              <div className="text-xs text-green-700">Pass Rate</div>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-lg font-bold text-blue-900">{(stats.averageScore * 100).toFixed(1)}%</div>
              <div className="text-xs text-blue-700">Avg Score</div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="text-lg font-bold text-purple-900">{stats.passedCount}</div>
              <div className="text-xs text-purple-700">Passed</div>
            </div>
            <div className="bg-red-50 p-3 rounded-lg">
              <div className="text-lg font-bold text-red-900">{stats.failedCount}</div>
              <div className="text-xs text-red-700">Failed</div>
            </div>
          </div>

          {/* Additional Details */}
          <div className="space-y-2">
          <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-600">Pass Threshold</span>
              <span className="text-sm font-medium text-gray-900">{(stats.threshold * 100).toFixed(0)}%</span>
            </div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Evaluation Model</span>
                <span className="text-xs font-mono text-gray-700">{stats.model}</span>
          </div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Criteria Count</span>
                <span className="text-sm font-medium text-gray-900">{stats.criteria.length}</span>
              </div>
              {stats.averageExecutionTime > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">Avg Execution Time</span>
                  <span className="text-sm font-medium text-gray-900">{stats.averageExecutionTime}ms</span>
                </div>
              )}
            </div>
          </div>

          {/* Criteria Details */}
          {stats.criteria.length > 0 && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-xs font-medium text-gray-700 mb-2">Evaluation Criteria</div>
              <div className="space-y-1">
                {stats.criteria.slice(0, 3).map((criterion, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-xs text-gray-600 truncate">{criterion.name}</span>
                    <span className="text-xs text-gray-500">Weight: {criterion.weight}</span>
                  </div>
                ))}
                {stats.criteria.length > 3 && (
                  <div className="text-xs text-gray-500 italic">
                    ... and {stats.criteria.length - 3} more criteria
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    } else if (businessMetrics.sourceNodeType === 'prompt') {
      return (
        <div className="space-y-4">
          <div className="text-xs font-medium text-gray-700">Prompt Node Summary</div>
          
          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-lg font-bold text-blue-900">{stats.totalTokens.toLocaleString()}</div>
              <div className="text-xs text-blue-700">Total Tokens</div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="text-lg font-bold text-green-900">{stats.averageTokens.toLocaleString()}</div>
              <div className="text-xs text-green-700">Avg Tokens</div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="text-lg font-bold text-purple-900">{stats.averageExecutionTime}ms</div>
              <div className="text-xs text-purple-700">Avg Time</div>
            </div>
            <div className="bg-orange-50 p-3 rounded-lg">
              <div className="text-lg font-bold text-orange-900">{stats.totalRequests}</div>
              <div className="text-xs text-orange-700">Requests</div>
            </div>
          </div>

          {/* Token Breakdown */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-indigo-50 p-3 rounded-lg">
              <div className="text-lg font-bold text-indigo-900">{stats.totalInputTokens.toLocaleString()}</div>
              <div className="text-xs text-indigo-700">Input Tokens</div>
            </div>
            <div className="bg-emerald-50 p-3 rounded-lg">
              <div className="text-lg font-bold text-emerald-900">{stats.totalOutputTokens.toLocaleString()}</div>
              <div className="text-xs text-emerald-700">Output Tokens</div>
            </div>
          </div>

          {/* Prompt Details */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-600">Model</span>
              <span className="text-sm font-medium text-gray-900">
                {stats.model.includes('llama') ? '🦙' : '🤖'} {stats.model}
              </span>
            </div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-600">Prompt</span>
              <span className="text-xs font-mono text-gray-700 truncate max-w-32" title={stats.promptName}>
                {stats.promptName}
              </span>
          </div>
            {stats.promptVersion !== 'N/A' && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Version</span>
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  v{stats.promptVersion}
                </span>
        </div>
            )}
          </div>
        </div>
      );
    } else {
      // Fallback for unknown types - show minimal stats
      return (
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-700">Data Summary</div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-sm text-gray-900">
              {stats.totalProperties || 0} properties detected
            </div>
          </div>
        </div>
      );
    }
  };

  const renderChart = () => {
    if (!processedData || !processedData.businessMetrics) return null;

    const { businessMetrics } = processedData;
    const chartData = businessMetrics.chartData || {};

    if (businessMetrics.sourceNodeType === 'autoEvaluator') {
      return (
        <div className="space-y-4">
          {/* Pass/Fail Pie Chart */}
          {chartData.passFailData && chartData.passFailData.length > 0 && (
          <div className="space-y-2">
              <div className="text-xs font-medium text-gray-700">Pass/Fail Distribution</div>
              <div className="h-40 flex justify-center">
                <Pie
                  data={{
                    labels: chartData.passFailData.map(item => item.label),
                    datasets: [
                      {
                        data: chartData.passFailData.map(item => item.value),
                        backgroundColor: chartData.passFailData.map(item => item.color),
                        borderWidth: 2,
                        borderColor: '#ffffff',
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          font: { size: 10 },
                          padding: 10,
                        },
                      },
                      tooltip: {
                        titleFont: { size: 11 },
                        bodyFont: { size: 10 },
                      },
                    },
                  }}
                />
                  </div>
                  </div>
          )}

          {/* Score Distribution Bar Chart */}
          {chartData.scoreDistribution && chartData.scoreDistribution.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-gray-700">Score Distribution</div>
              <div className="h-32">
                <Bar
                  data={{
                    labels: chartData.scoreDistribution.map(item => item.range),
                    datasets: [
                      {
                        label: 'Count',
                        data: chartData.scoreDistribution.map(item => item.count),
                        backgroundColor: chartData.scoreDistribution.map(item => item.color),
                        borderRadius: 4,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        titleFont: { size: 11 },
                        bodyFont: { size: 10 },
                      },
                    },
                    scales: {
                      x: {
                        ticks: { font: { size: 10 } },
                        grid: { display: false },
                      },
                      y: {
                        ticks: { font: { size: 10 } },
                        beginAtZero: true,
                      },
                    },
                  }}
                />
                      </div>
                      </div>
          )}

          {/* Score Timeline */}
          {chartData.scoreTimeline && chartData.scoreTimeline.length > 1 && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-gray-700">Score Trend</div>
              <div className="h-32">
                <Line
                  data={{
                    datasets: [
                      {
                        label: 'Score %',
                        data: chartData.scoreTimeline,
                        borderColor: 'rgb(59, 130, 246)',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        borderWidth: 2,
                        pointRadius: 3,
                        tension: 0.3,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        titleFont: { size: 11 },
                        bodyFont: { size: 10 },
                      },
                    },
                    scales: {
                      x: {
                        type: 'linear',
                        title: { display: true, text: 'Test Case', font: { size: 10 } },
                        ticks: { font: { size: 10 } },
                      },
                      y: {
                        title: { display: true, text: 'Score %', font: { size: 10 } },
                        ticks: { font: { size: 10 } },
                        min: 0,
                        max: 100,
                      },
                    },
                  }}
                />
              </div>
            </div>
          )}
        </div>
      );
    } else if (businessMetrics.sourceNodeType === 'prompt') {
      return (
        <div className="space-y-4">
          {/* Token Usage Breakdown */}
          {chartData.tokenUsageBreakdown && chartData.tokenUsageBreakdown.length > 0 && (
          <div className="space-y-2">
              <div className="text-xs font-medium text-gray-700">Token Usage Breakdown</div>
              <div className="h-40 flex justify-center">
                <Pie
                  data={{
                    labels: chartData.tokenUsageBreakdown.map(item => item.label),
                    datasets: [
                      {
                        data: chartData.tokenUsageBreakdown.map(item => item.value),
                        backgroundColor: chartData.tokenUsageBreakdown.map(item => item.color),
                        borderWidth: 2,
                        borderColor: '#ffffff',
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          font: { size: 10 },
                          padding: 10,
                        },
                      },
                      tooltip: {
                        titleFont: { size: 11 },
                        bodyFont: { size: 10 },
                        callbacks: {
                          label: function(context) {
                            return `${context.label}: ${context.parsed.toLocaleString()} tokens`;
                          }
                        }
                      },
                    },
                  }}
                />
                  </div>
                  </div>
          )}

          {/* Token Distribution Bar Chart */}
          {chartData.tokenDistribution && chartData.tokenDistribution.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-gray-700">Token Usage Distribution</div>
              <div className="h-32">
                <Bar
                  data={{
                    labels: chartData.tokenDistribution.map(item => item.range),
                    datasets: [
                      {
                        label: 'Requests',
                        data: chartData.tokenDistribution.map(item => item.count),
                        backgroundColor: chartData.tokenDistribution.map(item => item.color),
                        borderRadius: 4,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        titleFont: { size: 11 },
                        bodyFont: { size: 10 },
                      },
                    },
                    scales: {
                      x: {
                        title: { display: true, text: 'Token Range', font: { size: 10 } },
                        ticks: { font: { size: 10 } },
                        grid: { display: false },
                      },
                      y: {
                        title: { display: true, text: 'Count', font: { size: 10 } },
                        ticks: { font: { size: 10 } },
                        beginAtZero: true,
                      },
                    },
                  }}
                />
              </div>
            </div>
          )}

          {/* Performance Timeline */}
          {chartData.performanceTimeline && chartData.performanceTimeline.length > 1 && (
          <div className="space-y-2">
              <div className="text-xs font-medium text-gray-700">Performance Timeline</div>
              <div className="h-32">
                <Line
                  data={{
                    datasets: [
                      {
                        label: 'Execution Time (ms)',
                        data: chartData.performanceTimeline,
                        borderColor: 'rgb(34, 197, 94)',
                        backgroundColor: 'rgba(34, 197, 94, 0.1)',
                        borderWidth: 2,
                        pointRadius: 3,
                        tension: 0.3,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        titleFont: { size: 11 },
                        bodyFont: { size: 10 },
                      },
                    },
                    scales: {
                      x: {
                        type: 'linear',
                        title: { display: true, text: 'Request #', font: { size: 10 } },
                        ticks: { font: { size: 10 } },
                      },
                      y: {
                        title: { display: true, text: 'Time (ms)', font: { size: 10 } },
                        ticks: { font: { size: 10 } },
                        beginAtZero: true,
                      },
                    },
                  }}
                />
                  </div>
                  </div>
          )}
                </div>
              );
    } else {
      // Generic data type visualization
      if (chartData.typeDistribution && chartData.typeDistribution.length > 0) {
      return (
        <div className="space-y-4">
          <div className="text-xs font-medium text-gray-700">Data Type Distribution</div>
            <div className="h-40 flex justify-center">
              <Pie
                data={{
                  labels: chartData.typeDistribution.map(item => item.label),
                  datasets: [
                    {
                      data: chartData.typeDistribution.map(item => item.value),
                      backgroundColor: chartData.typeDistribution.map(item => item.color),
                      borderWidth: 2,
                      borderColor: '#ffffff',
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        font: { size: 10 },
                        padding: 10,
                      },
                    },
                  },
                }}
              />
                  </div>
                </div>
              );
      }
      
      return (
        <div className="text-center py-4 text-gray-500">
          <div className="text-xs">No chart data available</div>
        </div>
      );
    }
  };

  return (
    <div className={`
      bg-white border-2 rounded-2xl shadow-soft min-w-[420px] max-w-[600px] 
      transition-all duration-200 hover:shadow-medium
      ${getNodeBorderColor()}
    `}>
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        style={{ 
          background: '#3b82f6',
          width: '14px',
          height: '14px',
          border: '2px solid #1d4ed8',
          borderRadius: '50%'
        }}
        isConnectable={isConnectable}
      />
      
      {/* Node Header */}
      <div className="bg-gradient-to-r from-blue-100 to-blue-50 px-4 py-3 rounded-t-2xl border-b border-blue-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-1.5 bg-blue-200 rounded-lg">
              <BarChart3 className="h-4 w-4 text-blue-800" />
            </div>
            <div>
              <span className="text-sm font-semibold text-blue-900">
                {data.label || 'Visualizer Node'}
              </span>
              <div className="text-xs text-blue-700 opacity-75">
                Data Visualization
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {processedData && (
              <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                {processedData.type}
              </div>
            )}
            {executionStatus && (
              <div className="flex items-center">
                {getStatusIcon()}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Node Content */}
      <div className="p-4 space-y-3">
        {/* Visualization Type Selector */}
        <div className="flex items-center space-x-2">
          <span className="text-xs font-medium text-gray-700">View:</span>
          <div className="flex space-x-1">
            <button
              onClick={() => setVisualizationType('table')}
              className={`p-1 rounded text-xs ${
                visualizationType === 'table' 
                  ? 'bg-blue-100 text-blue-800' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Table View"
            >
              <Table className="h-3 w-3" />
            </button>
            <button
              onClick={() => setVisualizationType('summary')}
              className={`p-1 rounded text-xs ${
                visualizationType === 'summary' 
                  ? 'bg-blue-100 text-blue-800' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Summary View"
            >
              <Eye className="h-3 w-3" />
            </button>
            <button
              onClick={() => setVisualizationType('chart')}
              className={`p-1 rounded text-xs ${
                visualizationType === 'chart' 
                  ? 'bg-blue-100 text-blue-800' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Chart View"
            >
              <TrendingUp className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* Visualization Content */}
        <div className="border border-gray-200 rounded-lg p-3 min-h-64">
          {renderVisualization()}
        </div>

        {/* Expand/Collapse Button */}
        {processedData && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-center p-2 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <div className="flex items-center space-x-2">
              <span className="text-xs font-medium text-gray-600">
                {isExpanded ? 'Show Less' : 'Show More'}
              </span>
              {isExpanded ? (
                <ChevronUp className="h-3 w-3 text-gray-500" />
              ) : (
                <ChevronDown className="h-3 w-3 text-gray-500" />
              )}
            </div>
          </button>
        )}
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        style={{ 
          background: '#3b82f6',
          width: '14px',
          height: '14px',
          border: '2px solid #1d4ed8',
          borderRadius: '50%'
        }}
        isConnectable={isConnectable}
      />
    </div>
  );
};

export default VisualizerNode; 